/**
 * This is the main entrypoint file for the application.
 *
 * When loaded in the client side, the application is rendered in the
 * #root element.
 *
 * When the bundle created from this file is imported in the server
 * side, the exported `renderApp` function can be used for server side
 * rendering.
 *
 * Note that this file is required for the build process.
 */

// Dependency libs
import React from 'react';
import ReactDOMClient from 'react-dom/client';
import { loadableReady } from '@loadable/component';
import Cookies from 'js-cookie';

// Import default styles before other CSS-related modules are imported
// This ensures that the styles in marketplaceDefaults.css are included
// as first ones in the final build CSS build file.
import './styles/marketplaceDefaults.css';

// Configs and store setup
import appSettings from './config/settings';
import defaultConfig from './config/configDefault';
import { LoggingAnalyticsHandler, GoogleAnalyticsHandler } from './analytics/handlers';
import configureStore from './store';

// Utils
import { createInstance, types as sdkTypes } from './util/sdkLoader';
import { mergeConfig } from './util/configHelpers';
import { matchPathname } from './util/routes';
import * as apiUtils from './util/api';
import * as log from './util/log';
import { inspectCfDestination } from './util/cfOrder';
import { setAccessTokenRefresher } from './util/tphApi';

// Import relevant global duck files
import { authInfo } from './ducks/auth.duck';
import { fetchAppAssets } from './ducks/hostedAssets.duck';
import { fetchCurrentUser } from './ducks/user.duck';

// Route config
import routeConfiguration from './routing/routeConfiguration';
// App it self
import { ClientApp, renderApp } from './app';

const render = (store, shouldHydrate) => {
  // If the server already loaded the auth information, render the app
  // immediately. Otherwise wait for the flag to be loaded and render
  // when auth information is present.
  const state = store.getState();
  const cdnAssetsVersion = state.hostedAssets.version;
  const authInfoLoaded = state.auth.authInfoLoaded;
  const info = authInfoLoaded ? Promise.resolve({}) : store.dispatch(authInfo());

  info
    .then(() => {
      // Ensure that Loadable Components is ready
      // and fetch hosted assets in parallel before initializing the ClientApp
      return Promise.all([
        loadableReady(),
        store.dispatch(fetchAppAssets(defaultConfig.appCdnAssets, cdnAssetsVersion)),
        store.dispatch(fetchCurrentUser()).catch(e => {
          // A stale/revoked/expired session cookie yields a 403 here. A null current
          // user is an already-supported state (user.duck.js:210), and this value is
          // never read (the `cu` binding below is unused) — so the ONLY thing a
          // rejection can do is skip hydrateRoot and leave every button on the page
          // permanently disabled (Button.js:67). It must not be fatal.
          log.error(e, 'boot-fetch-current-user-failed');

          // Story 9.20 — the server has REJECTED this token, so stop presenting it.
          // NOTHING in the app or the SDK ever clears a rejected token: the only
          // tokenStore.removeToken() call in the whole SDK is in the
          // ClearTokenAfterRevoke interceptor, and that is wired ONLY into
          // logoutInterceptors (sdk.js:145-151) — someone who cannot log in cannot
          // log out. Left alone, this cookie 403s every currentUser call until its
          // 30-day expiry (browser_cookie_store.js:6).
          //
          // 🔴 401 or 403 ONLY. `e` is the storableError that .unwrap() re-throws
          // (user.duck.js:285,305), so `status` is the numeric HTTP status
          // (errors.js:428-443). A 5xx, a network error, a timeout and a
          // SerializedError all carry no 401/403 and MUST NOT clear: signing every
          // visitor out on a transient failure is worse than the bug being fixed.
          if (e?.status === 401 || e?.status === 403) {
            // Same key and same (default) options as the SDK's own removeToken —
            // `st-<clientId>-token`, path `/` (browser_cookie_store.js:3,7,8,15-17).
            Cookies.remove(`st-${appSettings.sdk.clientId}-token`);
            // Re-read the now-absent token so isAuthenticated stops claiming a
            // session we no longer hold. The duck's own authInfo() in its catch
            // (user.duck.js:282) already ran, BEFORE this removal, so it resolved
            // stale-true; this is the one that resyncs. Not chained into the boot —
            // this leg must still resolve null on the same tick it does today.
            // The .catch is belt-and-braces: authInfoThunk swallows its own errors
            // (auth.duck.js:46-54), so this cannot reject, but an unhandled
            // rejection would abort the whole jest process (scripts/test.js:11).
            store.dispatch(authInfo()).catch(() => {});
          }

          return null;
        }),
      ]);
    })
    .then(([_, fetchedAppAssets, cu]) => {
      const { translations: translationsRaw, ...rest } = fetchedAppAssets || {};
      // We'll handle translations as a separate data.
      // It's given to React Intl instead of pushing to config Context
      const translations = translationsRaw?.data || {};

      // Rest of the assets are considered as hosted configs
      const configEntries = Object.entries(rest);
      const hostedConfig = configEntries.reduce((collectedData, [name, content]) => {
        return { ...collectedData, [name]: content.data || {} };
      }, {});

      if (shouldHydrate) {
        const container = document.getElementById('root');

        ReactDOMClient.hydrateRoot(
          container,
          <ClientApp store={store} hostedTranslations={translations} hostedConfig={hostedConfig} />,
          { onRecoverableError: log.onRecoverableError }
        );
      } else {
        const container = document.getElementById('root');
        const root = ReactDOMClient.createRoot(container);
        root.render(
          <ClientApp store={store} hostedTranslations={translations} hostedConfig={hostedConfig} />
        );
      }
    })
    .catch(e => {
      log.error(e, 'browser-side-render-failed');
    });
};

const setupAnalyticsHandlers = googleAnalyticsId => {
  let handlers = [];

  // Log analytics page views and events in dev mode
  if (appSettings.dev) {
    handlers.push(new LoggingAnalyticsHandler());
  }

  // Add Google Analytics 4 (GA4) handler if tracker ID is found
  if (googleAnalyticsId) {
    if (googleAnalyticsId.indexOf('G-') !== 0) {
      console.warn(
        'Google Analytics 4 (GA4) should have measurement id that starts with "G-" prefix'
      );
    } else {
      handlers.push(new GoogleAnalyticsHandler());
    }
  }

  return handlers;
};

// If we're in a browser already, render the client application.
if (typeof window !== 'undefined') {
  // set up logger with Sentry DSN client key and environment
  log.setup();

  // Story 3.24 pass C — the boot sweep. Reading the post-purchase destination
  // clears it once it has expired (`cfOrder.js`), so this makes the next page
  // load in the browser the latest an expired record can sit in localStorage —
  // an abandoned claim no longer waits indefinitely for some later sign-in.
  inspectCfDestination();

  const baseUrl = appSettings.sdk.baseUrl ? { baseUrl: appSettings.sdk.baseUrl } : {};
  const assetCdnBaseUrl = appSettings.sdk.assetCdnBaseUrl
    ? { assetCdnBaseUrl: appSettings.sdk.assetCdnBaseUrl }
    : {};

  const preloadedState = window.__PRELOADED_STATE__ || '{}';
  const initialState = JSON.parse(preloadedState, sdkTypes.reviver);
  const sdk = createInstance({
    transitVerbose: appSettings.sdk.transitVerbose,
    clientId: appSettings.sdk.clientId,
    secure: appSettings.usingSSL,
    typeHandlers: apiUtils.typeHandlers,
    ...baseUrl,
    ...assetCdnBaseUrl,
  });

  // Ticket 35 — tphApi.js reads the access token from the SDK's cookie, which
  // only the SDK's own calls refresh. On a 401 from tph-api it runs this once:
  // the SDK's 401 here trades the refresh token for a new access token and
  // writes it to the same cookie, then tphApi.js retries once.
  setAccessTokenRefresher(() => sdk.currentUser.show());

  // Note: on localhost:3000, you need to use environment variable.
  const googleAnalyticsIdFromSSR = initialState?.hostedAssets?.googleAnalyticsId;
  const googleAnalyticsId = googleAnalyticsIdFromSSR || process.env.REACT_APP_GOOGLE_ANALYTICS_ID;
  const analyticsHandlers = setupAnalyticsHandlers(googleAnalyticsId);
  const store = configureStore({ initialState, sdk, analyticsHandlers });

  require('./util/polyfills');
  render(store, !!window.__PRELOADED_STATE__);

  if (appSettings.dev) {
    // Expose stuff for the browser REPL
    window.app = {
      appSettings,
      defaultConfig,
      sdk,
      sdkTypes,
      store,
    };
  }
}

// Show warning if CSP is not enabled
const CSP = process.env.REACT_APP_CSP;
const cspEnabled = CSP === 'block' || CSP === 'report';

if (CSP === 'report' && process.env.REACT_APP_ENV === 'production') {
  console.warn(
    'Your production environment should use CSP with "block" mode. Read more from: https://www.sharetribe.com/docs/ftw-security/how-to-set-up-csp-for-ftw/'
  );
} else if (!cspEnabled) {
  console.warn(
    "CSP is currently not enabled! You should add an environment variable REACT_APP_CSP with the value 'report' or 'block'. Read more from: https://www.sharetribe.com/docs/ftw-security/how-to-set-up-csp-for-ftw/"
  );
}

// Export the function for server side rendering.
export default renderApp;

// exporting matchPathname and configureStore for server side rendering.
// matchPathname helps to figure out which route is called and if it has preloading needs
// configureStore is used for creating initial store state for Redux after preloading
export {
  matchPathname,
  configureStore,
  routeConfiguration,
  defaultConfig,
  mergeConfig,
  fetchAppAssets,
};
